import { Link } from 'react-router-dom'

import { useBreadcrumbDisplayLayout } from '../hooks/useBreadcrumbDisplayLayout'
import type { DisplayBreadcrumbItem } from '../lib/breadcrumbDisplay'
import type { BreadcrumbItem } from '../types/breadcrumb'

type Props = {
  items: BreadcrumbItem[]
  className?: string
  dense?: boolean
  ariaLabel?: string
}

const linkBase =
  'rounded font-medium transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-accent'
const buttonBase =
  'rounded font-medium text-ink transition-colors hover:bg-surface-muted hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent'
const currentBase = 'font-medium text-ink'
const ellipsisClass = 'shrink-0 whitespace-nowrap text-ink-soft'

function segmentClass(
  ellipsize: boolean,
  isLast: boolean,
  ellipsizeMiddle: boolean,
  ellipsizeLast: boolean,
): string {
  const shouldEllipsize =
    ellipsize || (isLast ? ellipsizeLast : ellipsizeMiddle)
  if (!shouldEllipsize) {
    return 'shrink-0 whitespace-nowrap'
  }
  return 'min-w-0 max-w-[min(100%,14rem)] truncate whitespace-nowrap'
}

function CrumbSegment({
  item,
  dense,
  isLast,
  ellipsizeMiddle,
  ellipsizeLast,
}: {
  item: DisplayBreadcrumbItem
  dense?: boolean
  isLast: boolean
  ellipsizeMiddle: boolean
  ellipsizeLast: boolean
}) {
  const textCls = dense ? 'text-sm' : 'text-xs'
  const title = item.title ?? item.label
  const layoutCls = segmentClass(item.ellipsize ?? false, isLast, ellipsizeMiddle, ellipsizeLast)

  if (item.isEllipsis) {
    return (
      <span className={[ellipsisClass, textCls].join(' ')} title={title}>
        {item.displayLabel}
      </span>
    )
  }

  if (item.to != null && item.to !== '') {
    return (
      <Link
        to={item.to}
        title={title}
        className={[linkBase, layoutCls, textCls, 'hover:text-ink'].join(' ')}
      >
        {item.displayLabel}
      </Link>
    )
  }

  if (item.onNavigate) {
    return (
      <button
        type="button"
        title={title}
        onClick={item.onNavigate}
        className={[buttonBase, layoutCls, textCls].join(' ')}
      >
        {item.displayLabel}
      </button>
    )
  }

  return (
    <span className={[currentBase, layoutCls, textCls].join(' ')} title={title}>
      {item.displayLabel}
    </span>
  )
}

/**
 * 面包屑：宽度足够时完整展示；不足时优先隐藏首页与中间路径，保留模块根与当前页；仍不足则段内末尾省略。
 */
export function ResponsiveBreadcrumbNav({
  items,
  className,
  dense = false,
  ariaLabel = '面包屑',
}: Props) {
  const { navRef, rowRef, displayItems, ellipsizeMiddle, ellipsizeLast } = useBreadcrumbDisplayLayout(
    items,
    dense,
  )

  const navCls = [
    'flex min-w-0 flex-nowrap items-center overflow-hidden',
    dense ? 'text-sm leading-tight text-ink-soft' : 'text-[11px] leading-tight text-ink-soft sm:text-xs',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const rowCls = 'flex min-w-0 flex-1 flex-nowrap items-center gap-x-1.5'

  if (items.length === 0) {
    return (
      <nav ref={navRef} className={navCls} aria-label={ariaLabel}>
        <span className="pointer-events-none select-none opacity-0" aria-hidden>
          —
        </span>
      </nav>
    )
  }

  const lastIndex = displayItems.length - 1

  return (
    <nav ref={navRef} className={navCls} aria-label={ariaLabel}>
      <div ref={rowRef} className={rowCls}>
        {displayItems.map((c, i) => (
          <span
            key={`${c.label}-${i}-${c.isEllipsis ? 'ellipsis' : 'crumb'}`}
            className={[
              'inline-flex min-w-0 items-center gap-1.5',
              i === lastIndex ? 'min-w-0 shrink' : 'shrink-0',
            ].join(' ')}
          >
            {i > 0 ? (
              <span className="shrink-0 font-normal text-line" aria-hidden>
                /
              </span>
            ) : null}
            <CrumbSegment
              item={c}
              dense={dense}
              isLast={i === lastIndex}
              ellipsizeMiddle={ellipsizeMiddle}
              ellipsizeLast={ellipsizeLast}
            />
          </span>
        ))}
      </div>
    </nav>
  )
}
